import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Reveal, SectionHead } from './Reveal';
import { Plus, MessageCircle } from 'lucide-react';
import { WA_DEFAULT_LINK } from '../lib/lead';

const FAQS = [
  {
    q: 'Can Lok Adalat really reduce or dismiss my challan amount?',
    a: 'Yes. Lok Adalats are statutory forums under the Legal Services Authorities Act. When your challan is taken up, the bench can legally reduce the compounding amount substantially, or in deserving cases dispose of it for a nominal amount. Our advocates present your case to maximize this lawful reduction.',
  },
  {
    q: 'How do I verify if my challan is actually settled on the government portal?',
    a: 'After disposal we share your official court receipt. You can independently confirm it on Parivahan (echallan.parivahan.gov.in) or your state traffic portal by entering your vehicle number — the challan status shows "Disposed / Settled" with the court reference.',
  },
  {
    q: 'What documents are required to initiate disposal?',
    a: 'Just three things: your vehicle registration number (RC), the mobile number linked to the challan, and the challan or court notice photo if you have one. For disputed cases we may ask for a copy of your DL and an authorization to appear on your behalf.',
  },
  {
    q: 'Which states across India do you cover?',
    a: 'We operate pan-India. Virtual Courts are live in most states, and we track every state Lok Adalat calendar — Delhi, Uttar Pradesh, Maharashtra, Karnataka, Tamil Nadu, Telangana, Rajasthan, Gujarat, and more. Share your vehicle number and we will confirm the exact forum for your challan.',
  },
];

export default function Faq() {
  const [open, setOpen] = useState(0);

  return (
    <section id="faqs" className="py-20 lg:py-28 bg-[#F8FAFC]" data-testid="faq-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead
          center
          eyebrow="FAQs"
          title="Straight Answers Before You Settle"
          sub="Everything drivers ask us before handing over their challan."
        />

        <div className="mt-12 flex flex-col gap-3.5">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <Reveal key={f.q} delay={i * 0.07}>
                <div
                  className={`rounded-2xl border bg-white transition-colors duration-300 ${
                    isOpen ? 'border-brand-blue/35 shadow-lg shadow-brand-blue/5' : 'border-slate-200 hover:border-brand-blue/25'
                  }`}
                  data-testid={`faq-item-${i + 1}`}
                >
                  <button
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    className="w-full flex items-center justify-between gap-4 text-left px-6 sm:px-7 py-5"
                    aria-expanded={isOpen}
                    data-testid={`faq-question-${i + 1}`}
                  >
                    <span className="font-display text-lg sm:text-xl font-bold text-brand-ink leading-snug">{f.q}</span>
                    <motion.span
                      animate={{ rotate: isOpen ? 45 : 0 }}
                      transition={{ duration: 0.25 }}
                      className={`shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg ${
                        isOpen ? 'bg-brand-orange text-white' : 'bg-slate-100 text-slate-500'
                      }`}
                    >
                      <Plus size={17} strokeWidth={2.8} />
                    </motion.span>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.32, ease: [0.215, 0.61, 0.355, 1] }}
                        className="overflow-hidden"
                      >
                        <p className="px-6 sm:px-7 pb-6 text-sm sm:text-[15px] leading-relaxed text-slate-500 border-t border-slate-100 pt-4">
                          {f.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.2}>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 text-center">
            <span className="text-sm font-semibold text-slate-500">Still have questions?</span>
            <a
              href={WA_DEFAULT_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-brand-blue hover:bg-brand-bluedark text-white text-sm font-bold px-5 py-2.5 transition-all hover:-translate-y-0.5"
              data-testid="faq-whatsapp-cta"
            >
              <MessageCircle size={16} /> Ask us on WhatsApp
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
